import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from '../contexts/TranslationContext';

import { API_BASE } from '../lib/apiBase';
const API = `${API_BASE}/api`;

export default function MagicLinkCallbackPage() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('verifying'); // verifying | success | error
    const [errorMsg, setErrorMsg] = useState('');
    const navigate = useNavigate();
    const { login: setAuthState } = useAuth();

    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) {
            setStatus('error');
            setErrorMsg(t('auth.magic.no_token', 'No login token found in URL.'));
            return;
        }

        const verify = async () => {
            try {
                const res = await axios.post(`${API}/auth/magic-link/verify`, { token }, {
                    headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
                });
                const { token: jwt, user } = res.data;
                localStorage.setItem('token', jwt);
                axios.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
                setStatus('success');
                toast.success(t('auth.magic.welcome', 'Welcome, {name}!', { name: user.name }));
                setTimeout(() => navigate('/dashboard'), 1200);
            } catch (err) {
                setStatus('error');
                setErrorMsg(err.response?.data?.detail || t('auth.magic.invalid', 'Login link is invalid or expired.'));
            }
        };
        verify();
    }, [searchParams, navigate, t]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-background" data-testid="magic-link-callback">
            <div className="text-center space-y-4 max-w-sm">
                {status === 'verifying' && (
                    <>
                        <Loader2 className="h-10 w-10 animate-spin text-slate-400 mx-auto" />
                        <p className="text-slate-600 font-medium">{t('auth.magic.verifying', 'Verifying login link...')}</p>
                    </>
                )}
                {status === 'success' && (
                    <>
                        <CheckCircle className="h-10 w-10 text-green-500 mx-auto" />
                        <p className="text-slate-900 font-semibold">{t('auth.magic.success', 'Logged in successfully!')}</p>
                        <p className="text-slate-500 text-sm">{t('common.redirecting', 'Redirecting...')}</p>
                    </>
                )}
                {status === 'error' && (
                    <>
                        <XCircle className="h-10 w-10 text-red-500 mx-auto" />
                        <p className="text-slate-900 font-semibold">{t('auth.magic.failed', 'Login failed')}</p>
                        <p className="text-slate-500 text-sm">{errorMsg}</p>
                        <a href="/login" className="text-blue-600 hover:underline text-sm font-medium">{t('auth.back_to_login', 'Back to login')}</a>
                    </>
                )}
            </div>
        </div>
    );
}
